// Money — minor-unit arithmetic and formatting for the consumer app.
//
// 🔴 ORIGIN: this is a deliberate copy of the arithmetic half of
// `apps/admin/src/lib/money.ts` (M7e design note §2.2). The two apps share no
// domain package — `packages/ui` is a component library, not a money library —
// and the presentation halves genuinely differ (admin palette vs Fajr palette).
// **If you change the arithmetic here, change it there too.** At a third
// consumer, promote to `packages/money`.
//
// What this replaces: `$locale.billing.tsx` carried a private `formatMinor`
// that computed the right divisor and then did
// `.toFixed(...).replace(/\.00$/, '')`, so 1000 QAR minor units rendered as
// `10` while 1234 rendered as `12.34` — an internally inconsistent column of
// figures. No `.replace` here, ever.
//
// CLAUDE.md: amounts are ALWAYS integer minor units. Never store or pass a
// float; never do money maths in a component.

export type Currency = 'QAR' | 'AED' | 'SAR' | 'KWD' | 'BHD' | 'OMR' | 'USD';

// KWD, BHD and OMR are 3-decimal currencies (1 dinar = 1000 fils). Everything
// else we sell in is 2-decimal. Getting this wrong is a 10x money error, which
// is why it lives in exactly one place per app.
const THREE_DECIMAL: ReadonlySet<string> = new Set(['KWD', 'BHD', 'OMR']);

export function currencyDigits(currency: string): number {
  return THREE_DECIMAL.has(currency.toUpperCase()) ? 3 : 2;
}

/**
 * Format minor units as a decimal string WITHOUT a currency code.
 * `formatMinorAmount(1234, 'KWD') === '1.234'`
 * `formatMinorAmount(1000, 'QAR') === '10.00'`
 */
export function formatMinorAmount(amountMinor: number, currency: string): string {
  const digits = currencyDigits(currency);
  const negative = amountMinor < 0;
  const abs = Math.abs(Math.trunc(amountMinor));
  const scale = 10 ** digits;
  const whole = Math.floor(abs / scale);
  const frac = abs - whole * scale;
  // Group the integer part; the fraction is zero-padded to the currency's own
  // precision. Doing this by hand (rather than toFixed) keeps it exact for
  // large integers and avoids float rounding entirely.
  const grouped = whole.toLocaleString('en-US');
  const body = digits === 0 ? grouped : `${grouped}.${String(frac).padStart(digits, '0')}`;
  return negative ? `-${body}` : body;
}

/**
 * Format with the currency code, e.g. `'QAR 12.34'`. The code is used rather
 * than a symbol because we sell in seven currencies whose symbols collide
 * (three different "﷼"-family marks).
 */
export function formatMoney(amountMinor: number, currency: string): string {
  return `${currency.toUpperCase()} ${formatMinorAmount(amountMinor, currency)}`;
}

/**
 * Sum minor units that MUST share a currency. Mixing currencies in one total is
 * the single most dangerous thing a money screen can do — with seven currencies
 * and no FX table, a blended number would be fiction. Callers group by currency
 * first; this throws rather than silently producing that fiction.
 */
export function sumMinor(rows: ReadonlyArray<{ amountMinor: number; currency: string }>): {
  amountMinor: number;
  currency: string;
} | null {
  if (rows.length === 0) return null;
  const currency = rows[0]!.currency;
  let total = 0;
  for (const row of rows) {
    if (row.currency !== currency) {
      throw new Error(
        `sumMinor: refusing to add ${row.currency} to ${currency} — group by currency first`,
      );
    }
    total += row.amountMinor;
  }
  return { amountMinor: total, currency };
}

/** Group any amount-bearing rows by their currency, preserving order. */
export function groupByCurrency<T extends { currency: string }>(
  rows: readonly T[],
): Array<{
  currency: string;
  rows: T[];
}> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const list = out.get(row.currency);
    if (list) list.push(row);
    else out.set(row.currency, [row]);
  }
  return [...out.entries()].map(([currency, group]) => ({ currency, rows: group }));
}
